import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertTriangle,
  BarChart3,
  ChevronRight,
  Eye,
  Focus,
  Layers3,
  Play,
  RotateCcw,
  ShieldCheck,
  Monitor,
  Smartphone,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Progress } from '../ui/progress';
import { Badge } from '../ui/badge';

/**
 * Visual Search Exercise practice for Train Driver Psychometrics.
 *
 * This is original TDP practice content. It is designed around the reported
 * cognitive structure of visual monitoring, rapid comparison and divided
 * attention, but it is not an OPC replica and does not use an invented OPC
 * pass mark.
 */

const NAVY = '#09225f';
const ORANGE = '#ff970f';

const GLYPHS = [
  '▲',
  '■',
  '●',
  '◆',
  '✦',
  '✚',
  '⬟',
  '◢',
  '◉',
  '⬢',
  '★',
  '⬣',
];

const TRACK_POINTS = [
  { x: 92, y: 160 },
  { x: 145, y: 88 },
  { x: 255, y: 52 },
  { x: 390, y: 52 },
  { x: 500, y: 88 },
  { x: 552, y: 160 },
  { x: 500, y: 232 },
  { x: 390, y: 268 },
  { x: 255, y: 268 },
  { x: 145, y: 232 },
];

/**
 * Standard level progression:
 *
 * Level 1 = 3 mins / comparison 2.8 sec
 * Level 2 = 4 mins / comparison 2.6 sec
 * Level 3 = 5 mins / comparison 2.4 sec
 * Level 4 = 6 mins / comparison 2.2 sec
 * Level 5 = 7 mins / comparison 2.0 sec
 *
 * Monitoring remains fixed at 2.5 sec throughout.
 */
const LEVEL_CONFIGS = {
  1: {
    name: 'Accuracy',
    description:
      'Build accurate responses at a measured comparison pace.',
    access: 'Free credits',
    durationSeconds: 180,
    monitorMs: 2500,
    comparisonMs: 2800,
    anomalyRate: 0.1,
  },

  2: {
    name: 'Consistency',
    description:
      'Maintain accurate performance for longer with a modest increase in pace.',
    access: 'Paid subscription',
    durationSeconds: 240,
    monitorMs: 2500,
    comparisonMs: 2600,
    anomalyRate: 0.105,
  },

  3: {
    name: 'Concentration',
    description:
      'Sustain concentration as the practice period increases.',
    access: 'Paid subscription',
    durationSeconds: 300,
    monitorMs: 2500,
    comparisonMs: 2400,
    anomalyRate: 0.11,
  },

  4: {
    name: 'Endurance',
    description:
      'Longer sustained-attention practice with a faster visual-comparison pace.',
    access: 'Paid subscription',
    durationSeconds: 360,
    monitorMs: 2500,
    comparisonMs: 2200,
    anomalyRate: 0.11,
  },

  5: {
    name: 'Assessment Preparation',
    description:
      'Highest normal practice demand, with an optional full assessment mode.',
    access: 'Paid subscription',
    durationSeconds: 420,
    monitorMs: 2500,
    comparisonMs: 2000,
    anomalyRate: 0.105,

    full: {
      eventsPerPart: 140,
      monitorMs: 2500,
      comparisonMs: 2000,
      intensitySwitch: 0.55,
      anomalyRate: 0.105,
    },
  },
};

const createEmptyStats = () => ({
  monitoring: {
    trials: 0,
    anomalies: 0,
    hits: 0,
    misses: 0,
    falseAlarms: 0,
    correctRejections: 0,
    responseTimes: [],
  },

  comparison: {
    trials: 0,
    correct: 0,
    incorrect: 0,
    missed: 0,

    lowCorrect: 0,
    lowTotal: 0,

    highCorrect: 0,
    highTotal: 0,

    responseTimes: [],
  },

  combined: {
    monitoringTrials: 0,
    monitoringAnomalies: 0,
    monitoringHits: 0,
    monitoringMisses: 0,
    monitoringFalseAlarms: 0,
    monitoringCorrectRejections: 0,
    monitoringResponseTimes: [],

    comparisonTrials: 0,
    comparisonCorrect: 0,
    comparisonIncorrect: 0,
    comparisonMissed: 0,

    comparisonLowCorrect: 0,
    comparisonLowTotal: 0,

    comparisonHighCorrect: 0,
    comparisonHighTotal: 0,

    comparisonResponseTimes: [],
  },
});

const cloneStats = (stats) => ({
  monitoring: {
    ...stats.monitoring,
    responseTimes: [
      ...stats.monitoring.responseTimes,
    ],
  },

  comparison: {
    ...stats.comparison,
    responseTimes: [
      ...stats.comparison.responseTimes,
    ],
  },

  combined: {
    ...stats.combined,

    monitoringResponseTimes: [
      ...stats.combined.monitoringResponseTimes,
    ],

    comparisonResponseTimes: [
      ...stats.combined.comparisonResponseTimes,
    ],
  },
});

const randomItem = (items) =>
  items[
    Math.floor(
      Math.random() * items.length,
    )
  ];

const chooseDistinct = (
  count,
  poolSize = 6,
) => {
  const pool = Array.from(
    { length: poolSize },
    (_, index) => index,
  );

  const chosen = [];

  while (
    chosen.length < count &&
    pool.length
  ) {
    const index = Math.floor(
      Math.random() * pool.length,
    );

    chosen.push(
      pool.splice(index, 1)[0],
    );
  }

  return chosen.sort(
    (a, b) => a - b,
  );
};

const percentage = (
  numerator,
  denominator,
) => {
  if (!denominator) return 0;

  return Math.round(
    (numerator / denominator) * 100,
  );
};

const mean = (values) => {
  if (!values.length) return 0;

  return Math.round(
    values.reduce(
      (sum, value) =>
        sum + value,
      0,
    ) / values.length,
  );
};

const formatTime = (seconds) => {
  const safe = Math.max(
    0,
    Math.ceil(seconds),
  );

  const minutes = Math.floor(
    safe / 60,
  );

  const secs = safe % 60;

  return `${minutes}:${secs
    .toString()
    .padStart(2, '0')}`;
};

const descriptor = (value) => {
  if (value >= 95) {
    return 'Very strong';
  }

  if (value >= 88) {
    return 'Strong';
  }

  if (value >= 78) {
    return 'Consistent';
  }

  return 'Development area';
};

/**
 * Creates the visual comparison.
 *
 * IMPORTANT:
 * High intensity does NOT change the
 * presentation time.
 *
 * Difficulty increases by increasing
 * the amount of highlighted information.
 */
const createComparisonStimulus = (
  progressRatio,
  intensitySwitch = 0.55,
) => {
  const highIntensity =
    progressRatio >= intensitySwitch;

  const highlightedCount =
    highIntensity
      ? Math.random() < 0.7
        ? 3
        : 2
      : Math.random() < 0.75
        ? 2
        : 1;

  const left = Array.from(
    { length: 6 },
    () => randomItem(GLYPHS),
  );

  const right = Array.from(
    { length: 6 },
    () => randomItem(GLYPHS),
  );

  const leftHighlighted =
    chooseDistinct(
      highlightedCount,
    );

  const rightHighlighted =
    chooseDistinct(
      highlightedCount,
    );

  const shouldMatch =
    Math.random() < 0.5;

  const targetSequence =
    leftHighlighted.map(
      (index) => left[index],
    );

  rightHighlighted.forEach(
    (index, sequenceIndex) => {
      right[index] =
        targetSequence[
          sequenceIndex
        ];
    },
  );

  if (!shouldMatch) {
    const changePosition =
      Math.floor(
        Math.random() *
          highlightedCount,
      );

    const rightIndex =
      rightHighlighted[
        changePosition
      ];

    let replacement =
      right[rightIndex];

    while (
      replacement ===
      right[rightIndex]
    ) {
      replacement =
        randomItem(GLYPHS);
    }

    right[rightIndex] =
      replacement;
  }

  return {
    left,
    right,

    leftHighlighted,
    rightHighlighted,

    shouldMatch,
    highIntensity,

    responded: false,

    createdAt:
      performance.now(),
  };
};

/* =========================================================
   OPERATIONAL MONITORING DISPLAY
   ========================================================= */

const TrackDisplay = ({
  event,
  trackIndex,
}) => {
  const currentPoint =
    TRACK_POINTS[
      trackIndex
    ] || TRACK_POINTS[0];

  const duplicatePoint =
    TRACK_POINTS[
      (trackIndex + 4) %
        TRACK_POINTS.length
    ];

  const stateChange =
    event?.type ===
    'state-change';

  const duplicate =
    event?.type ===
    'duplicate';

  return (
    <div className="overflow-hidden rounded border border-slate-200 bg-slate-50">
      <svg
        viewBox="0 0 644 320"
        className="block w-full"
        role="img"
        aria-label="Operational monitoring loop"
      >
        <path
          d="M92 160 C92 78 173 40 255 40 H390 C472 40 552 78 552 160 C552 242 472 280 390 280 H255 C173 280 92 242 92 160 Z"
          fill="none"
          stroke="#c9d2df"
          strokeWidth="12"
          strokeLinecap="round"
        />

        <path
          d="M92 160 C92 78 173 40 255 40 H390 C472 40 552 78 552 160 C552 242 472 280 390 280 H255 C173 280 92 242 92 160 Z"
          fill="none"
          stroke="#ffffff"
          strokeWidth="5"
          strokeLinecap="round"
        />

        {TRACK_POINTS.map(
          (
            point,
            index,
          ) => (
            <g key={index}>
              <circle
                cx={point.x}
                cy={point.y}
                r="11"
                fill="#ffffff"
                stroke="#9caabe"
                strokeWidth="3"
              />

              <circle
                cx={point.x}
                cy={point.y}
                r="4"
                fill={NAVY}
              />
            </g>
          ),
        )}

        <circle
          cx={currentPoint.x}
          cy={currentPoint.y}
          r="12"
          fill={
            stateChange
              ? ORANGE
              : NAVY
          }
          stroke={
            stateChange
              ? '#fff1d9'
              : '#dbe4f3'
          }
          strokeWidth="4"
          style={{
            transition:
              'cx 280ms linear, cy 280ms linear, fill 120ms linear',
          }}
        />

        {duplicate && (
          <circle
            cx={
              duplicatePoint.x
            }
            cy={
              duplicatePoint.y
            }
            r="12"
            fill={ORANGE}
            stroke="#fff1d9"
            strokeWidth="4"
          />
        )}
      </svg>
    </div>
  );
};

/* =========================================================
   VISUAL COMPARISON
   ========================================================= */

const SymbolSet = ({
  label,
  items,
  highlighted,
}) => (
  <div>
    <div className="mb-2 text-center text-[11px] font-semibold uppercase tracking-wide text-slate-500">
      {label}
    </div>

    <div className="grid grid-cols-3 gap-2 rounded border border-slate-200 bg-slate-50 p-3">
      {items.map(
        (
          symbol,
          index,
        ) => {
          const active =
            highlighted.includes(
              index,
            );

          return (
            <div
              key={`${label}-${index}`}
              className={`relative flex min-h-12 items-center justify-center rounded border text-xl font-bold ${
                active
                  ? 'border-amber-300 bg-amber-50 text-[#09225f]'
                  : 'border-slate-200 bg-white text-[#09225f]'
              }`}
            >
              {symbol}

              {active && (
                <span className="pointer-events-none absolute inset-1 rounded-sm border border-amber-200" />
              )}
            </div>
          );
        },
      )}
    </div>
  </div>
);

const ComparisonDisplay = ({
  event,
}) => {
  if (!event) {
    return null;
  }

  return (
    <div>
      <div className="mb-3 flex items-start justify-between gap-3">
        <div>
          <div className="font-semibold text-[#09225f]">
            Visual Comparison
          </div>

          <div className="mt-1 text-xs text-slate-500">
            Do the highlighted
            symbols match?
          </div>
        </div>

        <Badge
          variant="outline"
          className={
            event.highIntensity
              ? 'border-amber-200 bg-amber-50 text-amber-700'
              : 'border-slate-200 bg-slate-50 text-slate-500'
          }
        >
          {event.highIntensity
            ? 'High intensity'
            : 'Low intensity'}
        </Badge>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <SymbolSet
          label="Set A"
          items={
            event.left
          }
          highlighted={
            event.leftHighlighted
          }
        />

        <SymbolSet
          label="Set B"
          items={
            event.right
          }
          highlighted={
            event.rightHighlighted
          }
        />
      </div>
    </div>
  );
};

const ResultMetric = ({
  label,
  value,
}) => (
  <div className="rounded border border-slate-200 bg-slate-50 p-4">
    <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">
      {label}
    </div>

    <div className="mt-1 text-2xl font-bold text-[#09225f]">
      {value}
    </div>
  </div>
);

/* =========================================================
   MAIN COMPONENT
   ========================================================= */

export const VisualSearchGame = ({
  level = 1,
  onComplete,
  onExit,
}) => {
  const config =
    LEVEL_CONFIGS[
      level
    ] ||
    LEVEL_CONFIGS[1];

  const supportsFullAssessment =
    level === 5;

  const [
    deviceMode,
    setDeviceMode,
  ] = useState(null);

  const [
    gameState,
    setGameState,
  ] = useState(
    'device_select',
  );

  const [
    mode,
    setMode,
  ] = useState(
    'standard',
  );

  const [
    phase,
    setPhase,
  ] = useState(1);

  const [
    trackIndex,
    setTrackIndex,
  ] = useState(0);

  const [
    monitorEvent,
    setMonitorEvent,
  ] = useState(null);

  const [
    comparisonEvent,
    setComparisonEvent,
  ] = useState(null);

  const [
    acceptedButton,
    setAcceptedButton,
  ] = useState(null);

  const [
    stats,
    setStats,
  ] = useState(
    createEmptyStats(),
  );

  const [
    remainingSeconds,
    setRemainingSeconds,
  ] = useState(0);

  const [
    progressPercent,
    setProgressPercent,
  ] = useState(0);

  const [
    monitorProgress,
    setMonitorProgress,
  ] = useState(0);

  const [
    comparisonProgress,
    setComparisonProgress,
  ] = useState(0);

  const statsRef =
    useRef(stats);

  const trackIndexRef =
    useRef(0);

  const monitorEventRef =
    useRef(null);

  const comparisonEventRef =
    useRef(null);

  const monitorTimerRef =
    useRef(null);

  const comparisonTimerRef =
    useRef(null);

  const phaseClockRef =
    useRef(null);

  const phaseStartRef =
    useRef(0);

  const runningRef =
    useRef(false);

  const finishingRef =
    useRef(false);

  const monitorCountRef =
    useRef(0);

  const comparisonCountRef =
    useRef(0);

  const fullMonitorDoneRef =
    useRef(false);

  const fullComparisonDoneRef =
    useRef(false);

  const isFullAssessment =
    supportsFullAssessment &&
    mode === 'full';

  useEffect(() => {
    statsRef.current =
      stats;
  }, [stats]);

  useEffect(() => {
    setMode('standard');
    setGameState('device_select');
    setPhase(1);
    setDeviceMode(null);

    setStats(
      createEmptyStats(),
    );
  }, [level]);

  /* =======================================================
     TIMER MANAGEMENT
     ======================================================= */

  const clearTimers =
    useCallback(() => {
      clearTimeout(
        monitorTimerRef.current,
      );

      clearTimeout(
        comparisonTimerRef.current,
      );

      clearInterval(
        phaseClockRef.current,
      );

      monitorTimerRef.current =
        null;

      comparisonTimerRef.current =
        null;

      phaseClockRef.current =
        null;
    }, []);

  useEffect(
    () => () =>
      clearTimers(),
    [clearTimers],
  );

  const updateStats =
    useCallback(
      (updater) => {
        setStats(
          (current) => {
            const next =
              updater(
                current,
              );

            statsRef.current =
              next;

            return next;
          },
        );
      },
      [],
    );

  /* =======================================================
     BUTTON ACKNOWLEDGEMENT
     ======================================================= */

  const acknowledge =
    useCallback(
      (buttonId) => {
        setAcceptedButton(
          buttonId,
        );

        window.setTimeout(
          () => {
            setAcceptedButton(
              (current) =>
                current ===
                buttonId
                  ? null
                  : current,
            );
          },
          220,
        );
      },
      [],
    );

  /* =======================================================
     MONITORING STIMULUS
     ======================================================= */

  const createMonitorStimulus =
    useCallback(() => {
      const anomalyRate =
        isFullAssessment
          ? config.full
              .anomalyRate
          : config.anomalyRate;

      const anomaly =
        Math.random() <
        anomalyRate;

      const type =
        anomaly
          ? randomItem([
              'skip',
              'state-change',
              'duplicate',
              'reverse',
            ])
          : 'normal';

      let nextIndex =
        (trackIndexRef.current +
          1) %
        TRACK_POINTS.length;

      if (
        type === 'skip'
      ) {
        nextIndex =
          (trackIndexRef.current +
            2) %
          TRACK_POINTS.length;
      }

      if (
        type === 'reverse'
      ) {
        nextIndex =
          (trackIndexRef.current -
            1 +
            TRACK_POINTS.length) %
          TRACK_POINTS.length;
      }

      trackIndexRef.current =
        nextIndex;

      setTrackIndex(
        nextIndex,
      );

      const nextEvent = {
        anomaly,
        type,

        responded: false,

        createdAt:
          performance.now(),
      };

      monitorEventRef.current =
        nextEvent;

      setMonitorEvent(
        nextEvent,
      );
    }, [
      config,
      isFullAssessment,
    ]);

  const closeMonitorStimulus =
    useCallback(
      (combined) => {
        const event =
          monitorEventRef.current;

        if (
          !event ||
          event.responded
        ) {
          return;
        }

        event.responded =
          true;

        updateStats(
          (current) => {
            const next =
              cloneStats(
                current,
              );

            if (combined) {
              next.combined
                .monitoringTrials +=
                1;

              if (
                event.anomaly
              ) {
                next.combined
                  .monitoringAnomalies +=
                  1;

                next.combined
                  .monitoringMisses +=
                  1;
              } else {
                next.combined
                  .monitoringCorrectRejections +=
                  1;
              }
            } else {
              next.monitoring
                .trials += 1;

              if (
                event.anomaly
              ) {
                next.monitoring
                  .anomalies +=
                  1;

                next.monitoring
                  .misses += 1;
              } else {
                next.monitoring
                  .correctRejections +=
                  1;
              }
            }

            return next;
          },
        );
      },
      [updateStats],
    );

  /* =======================================================
     COMPARISON STIMULUS
     ======================================================= */

  const buildComparison =
    useCallback(
      (
        progressRatio,
      ) => {
        const switchPoint =
          isFullAssessment
            ? config.full
                .intensitySwitch
            : 0.55;

        const next =
          createComparisonStimulus(
            progressRatio,
            switchPoint,
          );

        comparisonEventRef.current =
          next;

        setComparisonEvent(
          next,
        );
      },
      [
        config,
        isFullAssessment,
      ],
    );

  const closeComparisonStimulus =
    useCallback(
      (combined) => {
        const event =
          comparisonEventRef.current;

        if (
          !event ||
          event.responded
        ) {
          return;
        }

        event.responded =
          true;

        updateStats(
          (current) => {
            const next =
              cloneStats(
                current,
              );

            if (combined) {
              next.combined
                .comparisonTrials +=
                1;

              next.combined
                .comparisonMissed +=
                1;

              if (
                event.highIntensity
              ) {
                next.combined
                  .comparisonHighTotal +=
                  1;
              } else {
                next.combined
                  .comparisonLowTotal +=
                  1;
              }
            } else {
              next.comparison
                .trials += 1;

              next.comparison
                .missed += 1;

              if (
                event.highIntensity
              ) {
                next.comparison
                  .highTotal +=
                  1;
              } else {
                next.comparison
                  .lowTotal +=
                  1;
              }
            }

            return next;
          },
        );
      },
      [updateStats],
    );

  /* =======================================================
     USER RESPONSES
     ======================================================= */

  const handleMonitorResponse =
    useCallback(() => {
      if (
        !runningRef.current ||
        phase === 2
      ) {
        return;
      }

      const event =
        monitorEventRef.current;

      if (
        !event ||
        event.responded
      ) {
        return;
      }

      acknowledge(
        'monitor',
      );

      event.responded =
        true;

      const responseTime =
        Math.round(
          performance.now() -
            event.createdAt,
        );

      updateStats(
        (current) => {
          const next =
            cloneStats(
              current,
            );

          if (phase === 3) {
            next.combined
              .monitoringTrials +=
              1;

            if (
              event.anomaly
            ) {
              next.combined
                .monitoringAnomalies +=
                1;

              next.combined
                .monitoringHits +=
                1;

              next.combined.monitoringResponseTimes.push(
                responseTime,
              );
            } else {
              next.combined
                .monitoringFalseAlarms +=
                1;
            }
          } else {
            next.monitoring
              .trials += 1;

            if (
              event.anomaly
            ) {
              next.monitoring
                .anomalies +=
                1;

              next.monitoring
                .hits += 1;

              next.monitoring.responseTimes.push(
                responseTime,
              );
            } else {
              next.monitoring
                .falseAlarms +=
                1;
            }
          }

          return next;
        },
      );
    }, [
      acknowledge,
      phase,
      updateStats,
    ]);

  const handleComparisonResponse =
    useCallback(
      (
        responseMeansMatch,
      ) => {
        if (
          !runningRef.current ||
          phase === 1
        ) {
          return;
        }

        const event =
          comparisonEventRef.current;

        if (
          !event ||
          event.responded
        ) {
          return;
        }

        acknowledge(
          responseMeansMatch
            ? 'comparison-no-error'
            : 'comparison-error',
        );

        event.responded =
          true;

        const correct =
          responseMeansMatch ===
          event.shouldMatch;

        const responseTime =
          Math.round(
            performance.now() -
              event.createdAt,
          );

        updateStats(
          (current) => {
            const next =
              cloneStats(
                current,
              );

            if (
              phase === 3
            ) {
              next.combined
                .comparisonTrials +=
                1;

              if (correct) {
                next.combined
                  .comparisonCorrect +=
                  1;
              } else {
                next.combined
                  .comparisonIncorrect +=
                  1;
              }

              next.combined.comparisonResponseTimes.push(
                responseTime,
              );

              if (
                event.highIntensity
              ) {
                next.combined
                  .comparisonHighTotal +=
                  1;

                if (correct) {
                  next.combined
                    .comparisonHighCorrect +=
                    1;
                }
              } else {
                next.combined
                  .comparisonLowTotal +=
                  1;

                if (correct) {
                  next.combined
                    .comparisonLowCorrect +=
                    1;
                }
              }
            } else {
              next.comparison
                .trials += 1;

              if (correct) {
                next.comparison
                  .correct += 1;
              } else {
                next.comparison
                  .incorrect += 1;
              }

              next.comparison.responseTimes.push(
                responseTime,
              );

              if (
                event.highIntensity
              ) {
                next.comparison
                  .highTotal += 1;

                if (correct) {
                  next.comparison
                    .highCorrect +=
                    1;
                }
              } else {
                next.comparison
                  .lowTotal += 1;

                if (correct) {
                  next.comparison
                    .lowCorrect +=
                    1;
                }
              }
            }

            return next;
          },
        );
      },
      [
        acknowledge,
        phase,
        updateStats,
      ],
    );

  /* =======================================================
     FINISH PHASE
     ======================================================= */

  const finishPhase =
    useCallback(() => {
      if (
        !runningRef.current ||
        finishingRef.current
      ) {
        return;
      }

      finishingRef.current =
        true;

      runningRef.current =
        false;

      clearTimers();

      if (phase === 1) {
        closeMonitorStimulus(
          false,
        );
      }

      if (phase === 2) {
        closeComparisonStimulus(
          false,
        );
      }

      if (phase === 3) {
        closeMonitorStimulus(
          true,
        );

        closeComparisonStimulus(
          true,
        );
      }

      window.setTimeout(
        () => {
          finishingRef.current =
            false;

          if (phase < 3) {
            setPhase(
              (current) =>
                current + 1,
            );

            setGameState(
              'phase_intro',
            );
          } else {
            setGameState(
              'complete',
            );
          }
        },
        0,
      );
    }, [
      clearTimers,
      closeComparisonStimulus,
      closeMonitorStimulus,
      phase,
    ]);

  /* =======================================================
     STANDARD PRACTICE ENGINE
     ======================================================= */

  const scheduleStandardMonitor =
    useCallback(
      (combined) => {
        const tick = () => {
          if (
            !runningRef.current
          ) {
            return;
          }

          if (
            monitorEventRef.current
          ) {
            closeMonitorStimulus(
              combined,
            );
          }

          createMonitorStimulus();

          monitorCountRef.current +=
            1;

          setMonitorProgress(
            monitorCountRef.current,
          );

          monitorTimerRef.current =
            window.setTimeout(
              tick,
              2500,
            );
        };

        monitorTimerRef.current =
          window.setTimeout(
            tick,
            300,
          );
      },
      [
        closeMonitorStimulus,
        createMonitorStimulus,
      ],
    );

  const scheduleStandardComparison =
    useCallback(
      (
        combined,
        phaseDurationMs,
      ) => {
        const tick = () => {
          if (
            !runningRef.current
          ) {
            return;
          }

          if (
            comparisonEventRef.current
          ) {
            closeComparisonStimulus(
              combined,
            );
          }

          const elapsed =
            performance.now() -
            phaseStartRef.current;

          const ratio =
            Math.min(
              1,
              elapsed /
                phaseDurationMs,
            );

          buildComparison(
            ratio,
          );

          comparisonCountRef.current +=
            1;

          setComparisonProgress(
            comparisonCountRef.current,
          );

          comparisonTimerRef.current =
            window.setTimeout(
              tick,
              config.comparisonMs,
            );
        };

        comparisonTimerRef.current =
          window.setTimeout(
            tick,
            combined
              ? 700
              : 300,
          );
      },
      [
        buildComparison,
        closeComparisonStimulus,
        config.comparisonMs,
      ],
    );

  /* =======================================================
     FULL ASSESSMENT ENGINE
     ======================================================= */

  const checkFullCombinedFinished =
    useCallback(() => {
      if (
        fullMonitorDoneRef.current &&
        fullComparisonDoneRef.current
      ) {
        finishPhase();
      }
    }, [finishPhase]);

  const scheduleFullMonitor =
    useCallback(
      (combined) => {
        const target =
          config.full
            .eventsPerPart;

        const tick = () => {
          if (
            !runningRef.current
          ) {
            return;
          }

          if (
            monitorEventRef.current
          ) {
            closeMonitorStimulus(
              combined,
            );
          }

          if (
            monitorCountRef.current >=
            target
          ) {
            if (combined) {
              fullMonitorDoneRef.current =
                true;

              checkFullCombinedFinished();
            } else {
              finishPhase();
            }

            return;
          }

          monitorCountRef.current +=
            1;

          setMonitorProgress(
            monitorCountRef.current,
          );

          createMonitorStimulus();

          monitorTimerRef.current =
            window.setTimeout(
              tick,
              config.full
                .monitorMs,
            );
        };

        monitorTimerRef.current =
          window.setTimeout(
            tick,
            300,
          );
      },
      [
        checkFullCombinedFinished,
        closeMonitorStimulus,
        config,
        createMonitorStimulus,
        finishPhase,
      ],
    );

  const scheduleFullComparison =
    useCallback(
      (combined) => {
        const target =
          config.full
            .eventsPerPart;

        const tick = () => {
          if (
            !runningRef.current
          ) {
            return;
          }

          if (
            comparisonEventRef.current
          ) {
            closeComparisonStimulus(
              combined,
            );
          }

          if (
            comparisonCountRef.current >=
            target
          ) {
            if (combined) {
              fullComparisonDoneRef.current =
                true;

              checkFullCombinedFinished();
            } else {
              finishPhase();
            }

            return;
          }

          comparisonCountRef.current +=
            1;

          setComparisonProgress(
            comparisonCountRef.current,
          );

          buildComparison(
            comparisonCountRef.current /
              target,
          );

          comparisonTimerRef.current =
            window.setTimeout(
              tick,
              config.full
                .comparisonMs,
            );
        };

        comparisonTimerRef.current =
          window.setTimeout(
            tick,
            combined
              ? 700
              : 300,
          );
      },
      [
        buildComparison,
        checkFullCombinedFinished,
        closeComparisonStimulus,
        config,
        finishPhase,
      ],
    );

  /* =======================================================
     BEGIN PHASE
     ======================================================= */

  const beginPhase =
    useCallback(() => {
      clearTimers();

      runningRef.current =
        true;

      finishingRef.current =
        false;

      monitorCountRef.current =
        0;

      comparisonCountRef.current =
        0;

      fullMonitorDoneRef.current =
        false;

      fullComparisonDoneRef.current =
        false;

      trackIndexRef.current =
        0;

      monitorEventRef.current =
        null;

      comparisonEventRef.current =
        null;

      setTrackIndex(0);

      setMonitorEvent(
        null,
      );

      setComparisonEvent(
        null,
      );

      setAcceptedButton(
        null,
      );

      setMonitorProgress(
        0,
      );

      setComparisonProgress(
        0,
      );

      setProgressPercent(
        0,
      );

      setGameState(
        'playing',
      );

      /* FULL ASSESSMENT */

      if (
        isFullAssessment
      ) {
        setRemainingSeconds(
          0,
        );

        if (phase === 1) {
          scheduleFullMonitor(
            false,
          );
        } else if (
          phase === 2
        ) {
          scheduleFullComparison(
            false,
          );
        } else {
          /*
           * Combined full assessment:
           *
           * Monitoring remains 2.5 sec.
           * Comparison remains 2.0 sec.
           *
           * Both operate concurrently.
           */
          scheduleFullMonitor(
            true,
          );

          scheduleFullComparison(
            true,
          );
        }

        return;
      }

      /* STANDARD TIMED PRACTICE */

      const phaseDurationMs =
        (config.durationSeconds *
          1000) /
        3;

      phaseStartRef.current =
        performance.now();

      setRemainingSeconds(
        phaseDurationMs /
          1000,
      );

      phaseClockRef.current =
        window.setInterval(
          () => {
            const elapsedMs =
              performance.now() -
              phaseStartRef.current;

            const remainingMs =
              Math.max(
                0,
                phaseDurationMs -
                  elapsedMs,
              );

            const ratio =
              Math.min(
                1,
                elapsedMs /
                  phaseDurationMs,
              );

            setRemainingSeconds(
              remainingMs /
                1000,
            );

            setProgressPercent(
              ratio * 100,
            );

            if (
              elapsedMs >=
              phaseDurationMs
            ) {
              finishPhase();
            }
          },
          200,
        );

      if (phase === 1) {
        scheduleStandardMonitor(
          false,
        );
      } else if (
        phase === 2
      ) {
        scheduleStandardComparison(
          false,
          phaseDurationMs,
        );
      } else {
        scheduleStandardMonitor(
          true,
        );

        scheduleStandardComparison(
          true,
          phaseDurationMs,
        );
      }
    }, [
      clearTimers,
      config,
      finishPhase,
      isFullAssessment,
      phase,
      scheduleFullComparison,
      scheduleFullMonitor,
      scheduleStandardComparison,
      scheduleStandardMonitor,
    ]);

  /* =======================================================
     START LEVEL
     ======================================================= */

  const startLevel =
    useCallback(() => {
      clearTimers();

      const empty =
        createEmptyStats();

      statsRef.current =
        empty;

      setStats(empty);

      setPhase(1);

      setGameState(
        'phase_intro',
      );
    }, [clearTimers]);

  /* =======================================================
     KEYBOARD CONTROLS
     ======================================================= */

  useEffect(() => {
    const handleKeyDown =
      (event) => {
        if (
          event.repeat ||
          !runningRef.current
        ) {
          return;
        }

        if (
          event.code ===
          'Space'
        ) {
          event.preventDefault();

          handleMonitorResponse();
        }

        if (
          event.key.toLowerCase() ===
          'n'
        ) {
          handleComparisonResponse(
            true,
          );
        }

        if (
          event.key.toLowerCase() ===
          'e'
        ) {
          handleComparisonResponse(
            false,
          );
        }
      };

    window.addEventListener(
      'keydown',
      handleKeyDown,
    );

    return () =>
      window.removeEventListener(
        'keydown',
        handleKeyDown,
      );
  }, [
    handleComparisonResponse,
    handleMonitorResponse,
  ]);

  /* =======================================================
     RESULTS CALCULATION
     ======================================================= */

  const summary =
    useMemo(() => {
      const monitoringTotal =
        stats.monitoring
          .hits +
        stats.monitoring
          .misses +
        stats.monitoring
          .falseAlarms +
        stats.monitoring
          .correctRejections;

      const monitoringAccuracy =
        percentage(
          stats.monitoring
            .hits +
            stats.monitoring
              .correctRejections,
          monitoringTotal,
        );

      const anomalyDetection =
        percentage(
          stats.monitoring
            .hits,
          stats.monitoring
            .hits +
            stats.monitoring
              .misses,
        );

      const comparisonTotal =
        stats.comparison
          .correct +
        stats.comparison
          .incorrect +
        stats.comparison
          .missed;

      const comparisonAccuracy =
        percentage(
          stats.comparison
            .correct,
          comparisonTotal,
        );

      const combinedMonitoringTotal =
        stats.combined
          .monitoringHits +
        stats.combined
          .monitoringMisses +
        stats.combined
          .monitoringFalseAlarms +
        stats.combined
          .monitoringCorrectRejections;

      const combinedMonitoringAccuracy =
        percentage(
          stats.combined
            .monitoringHits +
            stats.combined
              .monitoringCorrectRejections,
          combinedMonitoringTotal,
        );

      const combinedComparisonTotal =
        stats.combined
          .comparisonCorrect +
        stats.combined
          .comparisonIncorrect +
        stats.combined
          .comparisonMissed;

      const combinedComparisonAccuracy =
        percentage(
          stats.combined
            .comparisonCorrect,
          combinedComparisonTotal,
        );

      const lowAccuracy =
        percentage(
          stats.comparison
            .lowCorrect,
          stats.comparison
            .lowTotal,
        );

      const highAccuracy =
        percentage(
          stats.comparison
            .highCorrect,
          stats.comparison
            .highTotal,
        );

      const primaryTaskCost =
        monitoringAccuracy -
        combinedMonitoringAccuracy;

      const secondaryTaskCost =
        comparisonAccuracy -
        combinedComparisonAccuracy;

      const workloadCost =
        lowAccuracy -
        highAccuracy;

      const falseAlarms =
        stats.monitoring
          .falseAlarms +
        stats.combined
          .monitoringFalseAlarms;

      const practiceIndex =
        Math.round(
          monitoringAccuracy *
            0.35 +
            comparisonAccuracy *
              0.25 +
            combinedMonitoringAccuracy *
              0.3 +
            combinedComparisonAccuracy *
              0.1,
        );

      return {
        monitoringAccuracy,
        anomalyDetection,
        comparisonAccuracy,

        combinedMonitoringAccuracy,
        combinedComparisonAccuracy,

        primaryTaskCost,
        secondaryTaskCost,

        workloadCost,
        falseAlarms,

        practiceIndex,

        monitoringResponseTime:
          mean(
            stats.monitoring
              .responseTimes,
          ),
      };
    }, [stats]);

  /* =======================================================
     PHASE INSTRUCTIONS
     ======================================================= */

  const phaseContent =
    useMemo(() => {
      if (phase === 1) {
        return {
          icon: Eye,

          title:
            'Operational Monitoring',

          subtitle:
            'Maintain active monitoring of a repetitive visual pattern',

          points: [
            'Watch the moving marker as it follows the established sequence.',

            'Respond only when you detect an unexpected change or anomaly.',

            isFullAssessment
              ? 'This part contains 140 operational events at a fixed 2.5-second monitoring cadence.'
              : 'The monitoring cadence remains fixed at 2.5 seconds at every level.',
          ],
        };
      }

      if (phase === 2) {
        return {
          icon: Focus,

          title:
            'Rapid Visual Comparison',

          subtitle:
            'Compare highlighted visual information accurately under time pressure',

          points: [
            'Two groups of symbols will be shown.',

            'Compare only the highlighted symbols, in order.',

            isFullAssessment
              ? 'This part contains 140 comparisons at 2.0 seconds each.'
              : `This level uses a fixed ${(
                  config.comparisonMs /
                  1000
                ).toFixed(
                  1,
                )}-second comparison interval.`,
          ],
        };
      }

      return {
        icon: Layers3,

        title:
          'Combined Task',

        subtitle:
          'Protect the primary monitoring task while completing a concurrent comparison task',

        points: [
          'Both tasks are presented together.',

          'Operational monitoring is the primary task.',

          isFullAssessment
            ? 'Full Assessment runs 140 monitoring events at 2.5 seconds and 140 comparison events at 2.0 seconds.'
            : 'The final third of the level measures how well you maintain the primary task under divided attention.',
        ],
      };
    }, [
      config.comparisonMs,
      isFullAssessment,
      phase,
    ]);

  const standardPhaseSeconds =
    config.durationSeconds /
    3;

  /* =======================================================
     FULL ASSESSMENT PROGRESS DISPLAY
     ======================================================= */

  const fullProgressLabel =
    useMemo(() => {
      if (
        !isFullAssessment
      ) {
        return '';
      }

      const target =
        config.full
          .eventsPerPart;

      if (phase === 1) {
        return `${monitorProgress} of ${target}`;
      }

      if (phase === 2) {
        return `${comparisonProgress} of ${target}`;
      }

      return `Monitoring ${monitorProgress}/${target} · Comparison ${comparisonProgress}/${target}`;
    }, [
      comparisonProgress,
      config,
      isFullAssessment,
      monitorProgress,
      phase,
    ]);

  const fullProgressValue =
    useMemo(() => {
      if (
        !isFullAssessment
      ) {
        return progressPercent;
      }

      const target =
        config.full
          .eventsPerPart;

      if (phase === 1) {
        return (
          (monitorProgress /
            target) *
          100
        );
      }

      if (phase === 2) {
        return (
          (comparisonProgress /
            target) *
          100
        );
      }

      return (
        ((monitorProgress +
          comparisonProgress) /
          (target * 2)) *
        100
      );
    }, [
      comparisonProgress,
      config,
      isFullAssessment,
      monitorProgress,
      phase,
      progressPercent,
    ]);

  /* =======================================================
     EXIT / COMPLETION
     ======================================================= */

  const handleExit =
    onExit ||
    (() => {
      runningRef.current =
        false;

      clearTimers();

      setGameState(
        'intro',
      );
    });

  const sendCompletion =
    () => {
      if (!onComplete) {
        return;
      }

      onComplete({
        level,
        mode,

        completed: true,

        /**
         * Retained for compatibility
         * with older parent code.
         *
         * This is NOT an OPC pass.
         */
        passed: true,

        score:
          summary.practiceIndex,
        
        // Added for leaderboard compatibility
        maxScore: 100,
        accuracy: summary.practiceIndex,

        details: {
          monitoringAccuracy:
            summary.monitoringAccuracy,

          anomalyDetection:
            summary.anomalyDetection,

          comparisonAccuracy:
            summary.comparisonAccuracy,

          combinedMonitoringAccuracy:
            summary.combinedMonitoringAccuracy,

          combinedComparisonAccuracy:
            summary.combinedComparisonAccuracy,

          primaryTaskCost:
            summary.primaryTaskCost,

          secondaryTaskCost:
            summary.secondaryTaskCost,

          workloadCost:
            summary.workloadCost,

          falseAlarms:
            summary.falseAlarms,

          raw:
            statsRef.current,
        },
      });
    };

  /* =======================================================
     DEVICE SELECTION SCREEN
     ======================================================= */

  if (
    gameState === 'device_select'
  ) {
    return (
      <div className="mx-auto max-w-3xl">
        <Card className="rounded-lg border-slate-200 bg-white shadow-sm">
          <CardContent className="p-6 sm:p-8">
            <div className="text-center mb-6">
              <Badge className="mb-3 border border-orange-200 bg-orange-50 text-orange-700 hover:bg-orange-50">
                Level {level}: {config.name}
              </Badge>
              
              <h2 className="text-2xl font-bold text-[#09225f] mb-2">
                Select Your Device
              </h2>
              <p className="text-sm text-slate-500">
                Choose how you're taking this test for the best experience
              </p>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <Button
                variant="outline"
                className="h-32 flex-col gap-3 hover:border-[#09225f] hover:bg-[#09225f]/5"
                onClick={() => {
                  setDeviceMode('desktop');
                  setGameState('intro');
                }}
                data-testid="vse-select-desktop"
              >
                <Monitor className="w-10 h-10 text-[#09225f]" />
                <div>
                  <p className="font-semibold">Laptop/Desktop</p>
                  <p className="text-xs text-slate-500">Keyboard & mouse</p>
                </div>
              </Button>
              
              <Button
                variant="outline"
                className="h-32 flex-col gap-3 hover:border-[#ff970f] hover:bg-[#ff970f]/5"
                onClick={() => {
                  setDeviceMode('mobile');
                  setGameState('intro');
                }}
                data-testid="vse-select-mobile"
              >
                <Smartphone className="w-10 h-10 text-[#ff970f]" />
                <div>
                  <p className="font-semibold">Mobile/Tablet</p>
                  <p className="text-xs text-slate-500">Touch controls</p>
                </div>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  /* =======================================================
     INTRO SCREEN
     ======================================================= */

  if (
    gameState === 'intro'
  ) {
    return (
      <div className="mx-auto max-w-3xl">
        <Card className="rounded-lg border-slate-200 bg-white shadow-sm">
          <CardContent className="p-6 sm:p-8">

            <div className="text-center">

              <Badge className="mb-3 border border-orange-200 bg-orange-50 text-orange-700 hover:bg-orange-50">
                Level {level}:{' '}
                {config.name}
              </Badge>

              <h2 className="text-2xl font-bold text-[#09225f]">
                Visual Search
                Exercise Practice
              </h2>

              <p className="mt-2 text-sm text-[#ff970f]">
                Develops skills used
                in visual search,
                sustained attention
                and divided attention
              </p>

            </div>

            <div className="mt-6 rounded bg-slate-50 p-5">

              <h3 className="mb-4 text-sm font-bold text-[#09225f]">
                How This Test Works
              </h3>

              <div className="space-y-3 text-sm text-slate-600">

                {[
                  'Monitor a repeating operational movement pattern and identify unexpected changes.',

                  'Compare two sets of symbols and decide whether the highlighted information matches.',

                  'Perform both tasks together. Operational monitoring is the primary task.',

                  'Higher levels become longer and gradually reduce comparison time. Monitoring remains fixed at 2.5 seconds.',
                ].map(
                  (
                    text,
                    index,
                  ) => (
                    <div
                      key={text}
                      className="flex items-start gap-3"
                    >

                      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#09225f] text-xs font-bold text-white">
                        {index + 1}
                      </span>

                      <span className="pt-0.5">
                        {text}
                      </span>

                    </div>
                  ),
                )}

              </div>

              <div className="mt-5 border-t border-slate-200 pt-4">

                <div className="text-sm font-bold text-[#09225f]">
                  Scoring:
                </div>

                <p className="mt-2 text-sm text-slate-600">
                  Results show TDP
                  practice metrics
                  including monitoring
                  accuracy, comparison
                  accuracy, anomaly
                  detection, false alarms
                  and performance under
                  divided attention.
                </p>

              </div>

              <div className="mt-4 flex items-start gap-2 rounded border border-amber-200 bg-amber-50 p-3 text-xs leading-5 text-amber-700">

                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />

                <span>
                  This is preparation
                  practice, not an official
                  OPC assessment. It is
                  designed to develop the
                  underlying
                  visual-monitoring and
                  divided-attention skills.
                </span>

              </div>

            </div>

            <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-3">

              <div className="rounded border border-slate-200 bg-slate-50 p-3 text-center">

                <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                  Practice duration
                </div>

                <div className="mt-1 font-bold text-[#09225f]">
                  {isFullAssessment
                    ? '140 / 140 / 140'
                    : `${
                        config.durationSeconds /
                        60
                      } min`}
                </div>

              </div>

              <div className="rounded border border-slate-200 bg-slate-50 p-3 text-center">

                <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                  Monitoring pace
                </div>

                <div className="mt-1 font-bold text-[#09225f]">
                  2.5 sec
                </div>

              </div>

              <div className="rounded border border-slate-200 bg-slate-50 p-3 text-center">

                <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                  Comparison pace
                </div>

                <div className="mt-1 font-bold text-[#09225f]">
                  {(
                    config.comparisonMs /
                    1000
                  ).toFixed(
                    1,
                  )}{' '}
                  sec
                </div>

              </div>

            </div>

            {supportsFullAssessment && (

              <div className="mt-5">

                <div className="mb-2 text-center text-sm font-bold text-[#09225f]">
                  Level 5 Mode
                </div>

                <div className="flex justify-center gap-2">

                  <button
                    type="button"
                    onClick={() =>
                      setMode(
                        'standard',
                      )
                    }
                    className={`rounded border px-4 py-2 text-sm font-medium ${
                      mode ===
                      'standard'
                        ? 'border-orange-200 bg-orange-50 text-orange-700'
                        : 'border-slate-200 bg-white text-[#09225f]'
                    }`}
                  >
                    Standard Practice
                  </button>

                  <button
                    type="button"
                    onClick={() =>
                      setMode(
                        'full',
                      )
                    }
                    className={`rounded border px-4 py-2 text-sm font-medium ${
                      mode ===
                      'full'
                        ? 'border-orange-200 bg-orange-50 text-orange-700'
                        : 'border-slate-200 bg-white text-[#09225f]'
                    }`}
                  >
                    Full Assessment
                  </button>

                </div>

              </div>

            )}

            {isFullAssessment && (

              <div className="mt-5 rounded border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">

                <strong>
                  Full Assessment:
                </strong>{' '}

                140 operational
                events, 140 visual
                comparisons and 140
                combined-task events.

                Monitoring remains at
                2.5 seconds and
                comparison at
                2.0 seconds.

              </div>

            )}

            <div className="mt-6 flex justify-center gap-3">

              <Button
                onClick={
                  startLevel
                }
                className="min-w-56 rounded-sm bg-[#09225f] text-white hover:bg-[#102d72]"
              >

                <Play className="mr-2 h-4 w-4" />

                {isFullAssessment
                  ? 'Begin Full Assessment Practice'
                  : `Begin Level ${level}`}

              </Button>

              {onExit && (

                <Button
                  onClick={
                    handleExit
                  }
                  variant="outline"
                  className="rounded-sm border-slate-300 text-[#09225f]"
                >
                  Exit
                </Button>

              )}

            </div>

          </CardContent>
        </Card>
      </div>
    );
  }

  /* =======================================================
     PART INTRO SCREEN
     ======================================================= */

  if (
    gameState ===
    'phase_intro'
  ) {
    const PhaseIcon =
      phaseContent.icon;

    return (
      <div className="mx-auto max-w-3xl">

        <Card className="rounded-lg border-slate-200 bg-white shadow-sm">

          <CardContent className="p-6 sm:p-8">

            <div className="text-center">

              <Badge className="mb-3 border border-orange-200 bg-orange-50 text-orange-700 hover:bg-orange-50">
                Part {phase} of 3
                {isFullAssessment
                  ? ' · Full Assessment'
                  : ''}
              </Badge>

              <div className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded bg-slate-100 text-[#09225f]">

                <PhaseIcon className="h-5 w-5" />

              </div>

              <h2 className="text-2xl font-bold text-[#09225f]">
                {
                  phaseContent.title
                }
              </h2>

              <p className="mt-2 text-sm text-[#ff970f]">
                {
                  phaseContent.subtitle
                }
              </p>

            </div>

            <div className="mt-6 rounded bg-slate-50 p-5">

              <h3 className="mb-4 text-sm font-bold text-[#09225f]">
                Your Task
              </h3>

              <div className="space-y-3 text-sm text-slate-600">

                {phaseContent.points.map(
                  (
                    point,
                    index,
                  ) => (

                    <div
                      key={point}
                      className="flex items-start gap-3"
                    >

                      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#09225f] text-xs font-bold text-white">
                        {index + 1}
                      </span>

                      <span className="pt-0.5">
                        {point}
                      </span>

                    </div>

                  ),
                )}

              </div>

            </div>

            {!isFullAssessment && (

              <div className="mt-4 text-center text-xs text-slate-500">
                This part lasts
                approximately{' '}
                {formatTime(
                  standardPhaseSeconds,
                )}.
              </div>

            )}

            <div className="mt-6 flex justify-center gap-3">

              <Button
                onClick={
                  beginPhase
                }
                className="min-w-52 rounded-sm bg-[#09225f] text-white hover:bg-[#102d72]"
              >

                <Play className="mr-2 h-4 w-4" />

                Begin Part {phase}

              </Button>

              <Button
                onClick={
                  handleExit
                }
                variant="outline"
                className="rounded-sm border-slate-300 text-[#09225f]"
              >
                Exit
              </Button>

            </div>

          </CardContent>
        </Card>

      </div>
    );
  }

  /* =======================================================
     RESULTS
     ======================================================= */

  if (
    gameState ===
    'complete'
  ) {
    return (
      <div className="mx-auto max-w-4xl">

        <Card className="rounded-lg border-slate-200 bg-white shadow-sm">

          <CardContent className="p-6 sm:p-8">

            <div className="text-center">

              <Badge className="mb-3 border border-orange-200 bg-orange-50 text-orange-700 hover:bg-orange-50">
                Level {level}
                {isFullAssessment
                  ? ' · Full Assessment'
                  : ''}{' '}
                complete
              </Badge>

              <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-slate-100 text-[#09225f]">

                <BarChart3 className="h-6 w-6" />

              </div>

              <h2 className="text-2xl font-bold text-[#09225f]">
                Practice Results
              </h2>

              <p className="mt-2 text-sm text-slate-500">
                These are TDP
                practice metrics, not
                OPC pass marks or
                standard scores.
              </p>

            </div>

            <div className="mt-6 grid grid-cols-2 gap-3 lg:grid-cols-4">

              <ResultMetric
                label="Monitoring"
                value={`${summary.monitoringAccuracy}%`}
              />

              <ResultMetric
                label="Comparison"
                value={`${summary.comparisonAccuracy}%`}
              />

              <ResultMetric
                label="Primary-task cost"
                value={`${summary.primaryTaskCost} pts`}
              />

              <ResultMetric
                label="False alarms"
                value={
                  summary.falseAlarms
                }
              />

            </div>

            <div className="mt-6 overflow-hidden rounded border border-slate-200">

              <div className="grid grid-cols-[1.45fr_.65fr_1.7fr] bg-slate-50 px-4 py-3 text-[10px] font-semibold uppercase tracking-wide text-slate-500">

                <div>
                  Measure
                </div>

                <div>
                  Result
                </div>

                <div>
                  Interpretation
                </div>

              </div>

              {[
                [
                  'Standalone anomaly detection',

                  `${summary.anomalyDetection}%`,

                  descriptor(
                    summary.anomalyDetection,
                  ),
                ],

                [
                  'Average anomaly response',

                  summary.monitoringResponseTime
                    ? `${summary.monitoringResponseTime} ms`
                    : '—',

                  'Interpret speed alongside detection accuracy',
                ],

                [
                  'Standalone comparison accuracy',

                  `${summary.comparisonAccuracy}%`,

                  descriptor(
                    summary.comparisonAccuracy,
                  ),
                ],

                [
                  'Comparison workload cost',

                  `${summary.workloadCost} pts`,

                  summary.workloadCost <=
                  5
                    ? 'Stable as visual load increased'
                    : 'Performance reduced as visual load increased',
                ],

                [
                  'Combined primary monitoring',

                  `${summary.combinedMonitoringAccuracy}%`,

                  descriptor(
                    summary.combinedMonitoringAccuracy,
                  ),
                ],

                [
                  'Combined secondary comparison',

                  `${summary.combinedComparisonAccuracy}%`,

                  descriptor(
                    summary.combinedComparisonAccuracy,
                  ),
                ],

                [
                  'Primary-task dual-task cost',

                  `${summary.primaryTaskCost} pts`,

                  summary.primaryTaskCost <=
                  5
                    ? 'Primary monitoring remained well protected'
                    : 'Primary monitoring reduced under divided attention',
                ],

                [
                  'Secondary-task dual-task cost',

                  `${summary.secondaryTaskCost} pts`,

                  summary.secondaryTaskCost >
                  summary.primaryTaskCost
                    ? 'Secondary performance was sacrificed more than primary monitoring'
                    : 'Check that primary monitoring remained prioritised',
                ],
              ].map(
                ([
                  label,
                  value,
                  interpretation,
                ]) => (

                  <div
                    key={
                      label
                    }
                    className="grid grid-cols-[1.45fr_.65fr_1.7fr] border-t border-slate-200 px-4 py-3 text-sm"
                  >

                    <div className="pr-3 text-slate-600">
                      {label}
                    </div>

                    <div className="font-semibold text-[#09225f]">
                      {value}
                    </div>

                    <div className="text-slate-500">
                      {
                        interpretation
                      }
                    </div>

                  </div>

                ),
              )}

            </div>

            <div className="mt-5 rounded border border-blue-100 bg-blue-50 p-4 text-sm leading-6 text-slate-600">

              {summary.primaryTaskCost <=
              5
                ? 'Your primary monitoring performance remained comparatively well protected when the comparison task was added. This is the behaviour the exercise is intended to reinforce.'
                : 'Your primary monitoring performance reduced when the comparison task was added. Focus on maintaining active monitoring rather than preserving secondary-task accuracy at all costs.'}

            </div>

            <div className="mt-6 flex flex-wrap justify-center gap-3">

              <Button
                onClick={
                  sendCompletion
                }
                className="rounded-sm bg-[#2dbb7f] text-white hover:bg-[#27a86f]"
              >

                <ShieldCheck className="mr-2 h-4 w-4" />

                Continue

              </Button>

              <Button
                onClick={() => {
                  const empty =
                    createEmptyStats();

                  statsRef.current =
                    empty;

                  setStats(
                    empty,
                  );

                  setPhase(1);

                  setGameState(
                    'phase_intro',
                  );
                }}
                variant="outline"
                className="rounded-sm border-slate-300 text-[#09225f]"
              >

                <RotateCcw className="mr-2 h-4 w-4" />

                Retry Level

              </Button>

            </div>

          </CardContent>
        </Card>

      </div>
    );
  }

  /* =======================================================
     ACTIVE TEST
     ======================================================= */

  const showMonitoring =
    phase === 1 ||
    phase === 3;

  const showComparison =
    phase === 2 ||
    phase === 3;

  return (
    <div className="mx-auto max-w-5xl">

      <Card className="rounded-lg border-slate-200 bg-white shadow-sm">

        <CardContent className="p-5">

          <div className="flex flex-wrap items-center justify-between gap-3 text-sm text-[#09225f]">

            <div className="font-semibold">

              {isFullAssessment
                ? fullProgressLabel
                : `${formatTime(
                    remainingSeconds,
                  )} remaining`}

            </div>

            <Badge
              variant="outline"
              className="border-slate-200 bg-white text-[#09225f]"
            >

              {phase === 1
                ? 'Operational monitoring'
                : phase === 2
                  ? 'Visual comparison'
                  : 'Combined task'}

            </Badge>

          </div>

          <Progress
            value={
              fullProgressValue
            }
            className="mt-3 h-1.5"
          />

          {phase === 3 && (

            <div className="mt-4 rounded border border-blue-100 bg-blue-50 px-4 py-3 text-xs text-slate-600">

              <strong className="text-[#09225f]">
                Primary task:
              </strong>{' '}

              Operational monitoring.
              Protect this task first
              if the two demands
              compete for your
              attention.

            </div>

          )}

          <div
            className={`mt-4 grid grid-cols-1 gap-4 ${
              phase === 3
                ? 'lg:grid-cols-2'
                : ''
            }`}
          >

            {showMonitoring && (

              <div className="rounded border border-slate-200 bg-white p-4">

                <div className="mb-3 flex items-start justify-between gap-3">

                  <div>

                    <div className="font-semibold text-[#09225f]">
                      Operational
                      Monitoring
                    </div>

                    <div className="mt-1 text-xs text-slate-500">
                      Monitor the
                      repeating
                      movement
                      pattern.
                    </div>

                  </div>

                  <Badge className="border border-blue-100 bg-blue-50 text-[#09225f] hover:bg-blue-50">
                    Primary task
                  </Badge>

                </div>

                <TrackDisplay
                  event={
                    monitorEvent
                  }
                  trackIndex={
                    trackIndex
                  }
                />

                <Button
                  onClick={
                    handleMonitorResponse
                  }
                  className={`mt-3 w-full rounded-sm border py-5 font-bold ${
                    acceptedButton ===
                    'monitor'
                      ? 'border-emerald-500 bg-emerald-500 text-white hover:bg-emerald-500'
                      : 'border-red-200 bg-red-50 text-red-700 hover:bg-red-100'
                  }`}
                >
                  ERROR / ANOMALY
                </Button>

              </div>

            )}

            {showComparison && (

              <div className="rounded border border-slate-200 bg-white p-4">

                <ComparisonDisplay
                  event={
                    comparisonEvent
                  }
                />

                <div className="mt-3 grid grid-cols-2 gap-2">

                  <Button
                    onClick={() =>
                      handleComparisonResponse(
                        true,
                      )
                    }
                    className={`rounded-sm border py-5 font-bold ${
                      acceptedButton ===
                      'comparison-no-error'
                        ? 'border-emerald-500 bg-emerald-500 text-white hover:bg-emerald-500'
                        : 'border-slate-300 bg-white text-[#09225f] hover:bg-slate-50'
                    }`}
                  >
                    NO ERROR
                  </Button>

                  <Button
                    onClick={() =>
                      handleComparisonResponse(
                        false,
                      )
                    }
                    className={`rounded-sm border py-5 font-bold ${
                      acceptedButton ===
                      'comparison-error'
                        ? 'border-emerald-500 bg-emerald-500 text-white hover:bg-emerald-500'
                        : 'border-slate-300 bg-white text-[#09225f] hover:bg-slate-50'
                    }`}
                  >
                    ERROR
                  </Button>

                </div>

              </div>

            )}

          </div>

          <div className="mt-4 text-center text-[10px] text-slate-400">
            Responses briefly
            illuminate green to
            confirm they were
            recorded. This does not
            indicate whether the
            response was correct.
          </div>

        </CardContent>
      </Card>

    </div>
  );
};

export default VisualSearchGame;